import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ClientError } from '~/errors';
import { sanitizeTagValue } from '~/utils/instrumentation';
import { createLogger } from '~/utils/logging';
import { useHttpClient } from '~/hooks/useHttpClient';
import { useLocale } from '~/hooks/useLocale';
import { formatDateToYearMonthDayString } from '~/utils/date';

import { QUERY_KEY as REVIEW_DAY_STATUS_QUERY_KEY } from './useReviewDayStatusQuery';
import { invalidateDependentReviewQueries } from './reviewQueryInvalidation';

const log = createLogger('useReviewDeleteMutation');

export type DeleteReviewRequest = {
  periodId: string;
  date: Date;
};

export type DeleteReviewResponse = {
  deletedMeetingIds: string[];
};

export function useReviewDeleteMutation() {
  const httpClient = useHttpClient();
  const queryClient = useQueryClient();
  const locale = useLocale();

  const { data, error, mutate, reset, status } = useMutation({
    mutationFn: async (request: DeleteReviewRequest) => {
      log.debug('Deleting review', { request });
      const { date, periodId } = request;

      const requestBody = {
        date: formatDateToYearMonthDayString(date),
        periodId,
        tz: locale?.timeZone ?? '',
      };

      const { deletedMeetingIds } = await httpClient.post<DeleteReviewResponse>('/review/delete', requestBody);
      log.info('Deleted review successfully', {
        deletedMeetingCount: deletedMeetingIds.length,
      });
      return { deletedMeetingIds };
    },
    onSuccess: () => {
      invalidateDependentReviewQueries(queryClient);
      queryClient.invalidateQueries({ queryKey: [REVIEW_DAY_STATUS_QUERY_KEY] });
    },
    onError: (error: Error) => {
      if (error instanceof ClientError) {
        // Ignore client errors.
      } else {
        log.error('Failed to delete review', {
          errorMessage: sanitizeTagValue(error.message),
        });
      }
    },
  });

  return {
    data,
    error,
    mutate,
    reset,
    status,
  };
}
